import cx from 'classnames'
import React, { ChangeEvent, InputHTMLAttributes } from 'react'
import { useFormContext } from 'react-hook-form'

import { countryPrefixes } from '@f/elements/common/_country-prefixes'
import SVGFlags from '@f/elements/common/_flags'
import { FormErrorMessage } from '@f/elements/HookForm/ErrorMessage'

interface PhoneInputProps {
  phoneName: string
  countryName: string
  className?: string
  disabled?: boolean
  placeholder?: string
  phoneCountryPrefixProps?: InputHTMLAttributes<HTMLSelectElement> & {
    'data-sel': string
  }
  inputProps?: InputHTMLAttributes<HTMLInputElement> & {
    'data-sel': string
    'data-testid'?: string
  }
}

export const PhoneInput = ({
  phoneName,
  countryName,
  disabled,
  className,
  placeholder,
  phoneCountryPrefixProps,
  inputProps
}: PhoneInputProps) => {
  const { register, formState, getFieldState, getValues } = useFormContext()
  const { isValidating } = formState
  const { error, isTouched, isDirty } = getFieldState(phoneName, formState)
  const countryCode = getValues(countryName)
  const phoneValue = getValues(phoneName)

  const displayError = error?.message as string
  const isValid = isDirty && !displayError

  const handleInputFocus = () => {
    window._wt({
      category: 'input',
      action: 'focus',
      values: [{ type: 'name', value: phoneName }]
    })
  }

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { selectedIndex } = event.target.options
    const option = event.target.options[selectedIndex]

    const label = option?.label || event.target?.selectedOptions[0]?.text

    window._wt({
      category: 'select',
      action: 'change',
      values: [{ type: 'text', value: label }]
    })
  }

  return (
    <div className='PhoneInput-wrapper'>
      <div
        className={cx(
          'PhoneInput-inputs',
          displayError ? 'f-c-bo-war f-m-b-010' : 'f-m-b-020 f-c-bo-neu050',
          isValid && 'f-c-bo-suc400'
        )}>
        <div className='PhoneInput-country-wrapper'>
          <select
            {...register(countryName, {
              onChange: handleSelectChange
            })}
            {...phoneCountryPrefixProps}
            className='FormPhone-country-select'
            autoComplete='forceTurnOffAutoComplete'
            data-sel='slct-pctry'>
            {countryPrefixes.map(country => (
              <option
                key={`country-phone-dropdown-${country.code}`}
                value={country.code}>
                {country.country}
              </option>
            ))}
          </select>
          <SVGFlags
            className='FormPhone-country-select-flag'
            countryCode={countryCode}
          />
          <span className='[ f-t-body f-c-fo-pri f-m-l-005 ]'>
            {
              countryPrefixes.find(country => country.code === countryCode)
                ?.prefix
            }
          </span>
        </div>
        <label className='PhoneInput-phone' onFocus={handleInputFocus}>
          <span
            className={cx('[ f-pos-ab f-c-fo-mut f-t-body ] FormTextLabel', {
              'has-value': phoneValue
            })}>
            {placeholder}
          </span>
          <input
            className={cx(
              'FormText',
              disabled && 'FormText--disabled',
              isValidating && 'is-validating',
              isTouched && !displayError && 'f-c-bo-suc400',
              className
            )}
            maxLength={5000}
            {...register(phoneName)}
            {...inputProps}
          />
        </label>
      </div>
      {displayError && <FormErrorMessage>{displayError}</FormErrorMessage>}
    </div>
  )
}
